import React from "react";
import { translateService, dateHelperService } from "../../services/imports";

// Signatures panel of docs/RUNBOOK-document-page.md §3.3 (DP3). Until the
// validation result carries the platform's "signed by" identity (VR4), a row
// shows the certificate fields; validation details sit behind "details".

// the backend once sent `Signatures` (RUNBOOK E-D6); tolerate both and null
export const signaturesOf = (result) => result?.signatures || result?.Signatures || [];

const formatTime = (value) =>
  value ? dateHelperService.format(value, dateHelperService.dateTimeFormat, false, true) : "";

const statusClass = (valid) => (valid === true ? "valid" : "novalid");

// the icons are CSS backgrounds; screen readers get the words
const statusText = (valid) => (
  <span className="sr-only">
    {" "}
    {translateService.t(valid === true ? "document.valid" : "document.notValid")}
  </span>
);

export default class SignaturesPanel extends React.Component {
  constructor(props) {
    super(props);
    this.state = { open: {} };
  }

  toggle(key) {
    this.setState((prev) => ({ open: { ...prev.open, [key]: !prev.open[key] } }));
  }

  renderTimestamps(signature) {
    const timestamps = signature?.timestamps || [];
    return (
      <div className="signatures-panel__group">
        <div className="signatures-panel__group-title">
          {translateService.t("result.timeStamps")}
        </div>
        {timestamps.map((tms, index) => (
          <React.Fragment key={tms.id || `tms-${index}`}>
            {tms.id && (
              <React.Fragment>
                <div className={`keyValue ${statusClass(tms?.valid)}`}>
                  {translateService.t("result.productDate")} {formatTime(tms?.productionDate)}
                  {statusText(tms?.valid)}
                </div>
                <div className={`keyValue ${tms?.signingCertValid ? "" : "color-three"}`}>
                  {translateService.t(
                    `result.sertificaTe${tms?.signingCertValid ? "IS" : "NO"}Valid`
                  )}
                </div>
                <div className="keyValue">{tms?.signingCertIssuerDN}</div>
              </React.Fragment>
            )}
            {tms?.notes && <div className="keyValue loading">{tms.notes}</div>}
          </React.Fragment>
        ))}
      </div>
    );
  }

  renderRevocations(signature) {
    const revocations = signature?.revocations || [];
    return (
      <div className="signatures-panel__group">
        <div className="signatures-panel__group-title">
          {translateService.t("result.revocations")}
        </div>
        {revocations.map((revc, index) => (
          <div
            className={`rev-item ${
              revc.id ? (revc?.valid === true && !revc?.revoked ? "valid" : "novalid") : ""
            }`}
            key={revc.id || `rev-${index}`}
          >
            {revc.id && revc?.revocationDate && (
              <div className="keyValue">
                {translateService.t("result.productDate")} {formatTime(revc.revocationDate)}
              </div>
            )}
            {revc.id && <div className="keyValue">{revc?.signingCertIssuerDN}</div>}
            {revc.id && revc?.valid === true && !revc?.revoked && statusText(true)}
            {revc.id && revc?.revoked && (
              <div className="keyValue color-three">{translateService.t("dashboard.revoked")}</div>
            )}
            {revc.id && !revc?.valid && !revc?.revoked && (
              <div className="keyValue color-three">
                {translateService.t("common.revokeStatusUnknown")}
              </div>
            )}
            {revc?.notes && <div className="keyValue loading">{revc.notes}</div>}
          </div>
        ))}
      </div>
    );
  }

  renderDetails(signature, id) {
    const timestampClass =
      signature?.timeStampCount > 0 ? statusClass(signature?.timeStampValid) : "";
    return (
      <div id={id} className="signatures-panel__details">
        <dl className="signatures-panel__facts">
          {signature?.lastLTATime && (
            <div>
              <dt>{translateService.t("result.lastArchiveTime")}</dt>
              <dd>{formatTime(signature.lastLTATime)}</dd>
            </div>
          )}
          <div className={timestampClass}>
            <dt>{translateService.t("result.timestampCount")}</dt>
            <dd>
              {signature?.timeStampCount}
              {timestampClass && statusText(signature?.timeStampValid)}
            </dd>
          </div>
          {signature?.validTimeStampCount !== signature?.timeStampCount && (
            <div>
              <dt>{translateService.t("result.validTimestampCount")}</dt>
              <dd>{signature?.validTimeStampCount}</dd>
            </div>
          )}
          <div className={signature?.certRevoced === true ? "novalid" : ""}>
            <dt>{translateService.t("result.revocationCount")}</dt>
            <dd>
              {signature?.revocationCount}
              {signature?.certRevoced === true && (
                <span className="sr-only"> {translateService.t("dashboard.revoked")}</span>
              )}
            </dd>
          </div>
        </dl>
        {this.renderTimestamps(signature)}
        {this.renderRevocations(signature)}
        <div className="signatures-panel__group">
          <div className="signatures-panel__group-title">
            {translateService.t("result.issuerCertDN")}
          </div>
          <div className="keyValue">{signature?.issuerCertDN}</div>
        </div>
        <div className="signatures-panel__group">
          <div className="signatures-panel__group-title">
            {translateService.t("result.diagnosticData")}
          </div>
          <div className="keyValue">{signature?.signatureWarning}</div>
        </div>
      </div>
    );
  }

  renderSignature(signature, index) {
    const key = signature?.id || `signature-${index}`;
    const open = !!this.state.open[key];
    const valid = signature?.validSignature === true;
    const identity = [signature?.signerCN, signature?.signerAltName, signature?.signerPID]
      .filter(Boolean)
      .join(" ");
    const detailsId = `signature-details-${index}`;

    return (
      <li key={key} className={`signatures-panel__item ${valid ? "is-valid" : "is-invalid"}`}>
        <div className="signatures-panel__identity" title={signature?.signerDN}>
          {identity}
        </div>
        <div className="signatures-panel__meta">
          <span className={`signatures-panel__badge ${statusClass(signature?.validSignature)}`}>
            {translateService.t(valid ? "document.valid" : "document.notValid")}
          </span>
          <span className="signatures-panel__time">
            {translateService.t("result.signatureTime").trim()} {formatTime(signature?.signatureTime)}
          </span>
        </div>
        <div className="signatures-panel__actions">
          <button
            type="button"
            className="signatures-panel__toggle"
            aria-expanded={open}
            aria-controls={detailsId}
            onClick={() => this.toggle(key)}
          >
            {translateService.t(open ? "document.hideDetails" : "document.details")}
          </button>
          {signature?.signatureRevisionUrl && (
            <button
              type="button"
              className="signatures-panel__download"
              onClick={() =>
                this.props.onDownload(signature.signatureRevisionUrl, `${signature.id}.pdf`)
              }
            >
              {translateService.t("common.download")}
            </button>
          )}
        </div>
        {open && this.renderDetails(signature, detailsId)}
      </li>
    );
  }

  render() {
    const { result } = this.props;
    const signatures = signaturesOf(result);

    return (
      <div className="signatures-panel">
        {result?.processingStatus === 0 && (
          <div className="signatures-panel__processing" role="status">
            {translateService.t("result.docIdLoading")}
          </div>
        )}
        <dl className="signatures-panel__summary">
          <div>
            <dt>{translateService.t("result.signatureCount")}</dt>
            <dd>{result?.signatureCount}</dd>
          </div>
          <div>
            <dt>{translateService.t("result.validSignatureCount")}</dt>
            <dd>{result?.validSignatureCount}</dd>
          </div>
          <div>
            <dt>{translateService.t("result.validTimestamp")}</dt>
            <dd>{formatTime(result?.validationTime)}</dd>
          </div>
        </dl>
        {result && result.processingStatus !== 0 && signatures.length === 0 && (
          <p className="signatures-panel__empty">{translateService.t("document.noSignatures")}</p>
        )}
        <ul className="signatures-panel__list">
          {signatures.map((signature, index) => this.renderSignature(signature, index))}
        </ul>
      </div>
    );
  }
}
